import { useDependency } from '@/scopes/common/infrastructure/dependencies';
import { useFiltersQuery } from '@/domain/prices/filters/hooks';
import type { FilterType } from '@/domain/prices/filters/types';
import { useAccommodationContext } from '@/react-pages/Prices/AccommodationContext';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { AlternativeFilter } from './types';
import { useEffect } from 'react';

export function usePricesQuery() {
  const pricesAPIClient = useDependency('pricesAPIClient');
  const { contextItemId, accoId, bookingGateId } = useAccommodationContext();

  const {
    isLoading: isLoadingFilters,
    isFetching: isFetchingFilters,
    filterSelection,
    isError,
    isEmptyResponse,
    data,
  } = useFiltersQuery();

  const filtersCriteria = { contextItemId, bookingGateId, accoId, filterSelection };

  const isEnabled =
    Boolean(accoId) && Boolean(data) && !isFetchingFilters && !isLoadingFilters && !isError && !isEmptyResponse;

  return useQuery({
    queryKey: ['prices', JSON.stringify(filtersCriteria)],
    queryFn: () => pricesAPIClient.getPrices(filtersCriteria),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
    retry: false,
    enabled: isEnabled,
  });
}

export function usePrices() {
  const { data, isLoading, isFetching, isError } = usePricesQuery();

  return {
    prices: data?.prices ?? [],
    pricesByDuration: data?.pricesByDuration ?? {},
    pricesByDurationAndDate: data?.pricesByDurationAndDate ?? {},
    dates: data?.dates ?? [],
    year: data?.year ?? 0,
    bestValueThreshold: data?.bestValueThreshold,
    highlightInfo: data?.highlightInfo,
    messages: data?.messages,
    alternativeFilters: data?.alternativeFilters,
    isLoading,
    isFetching,
    isError,
  };
}

export function useAlternativeFilter(filterName: keyof FilterType): AlternativeFilter | null {
  const { alternativeFilters } = usePrices();

  return alternativeFilters?.[filterName] ?? null;
}

export function useScrollToSection(loading: boolean, id: string) {
  useEffect(() => {
    const selector = document.querySelector(`#${id}`);
    if (selector && !loading) {
      selector?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loading]);
}
